/**
 * Logica di validazione per le note di rifiuto.
 *
 * Due tipi di rifiuto:
 *  - RIFIUTATO       → HQ rifiuta i documenti      → campo noteRifiuto
 *  - RIFIUTATO_DITTA → Ditta rifiuta il preventivo → campo noteRifiutoDitta
 */

import { WORKFLOW_STATES } from './workflow';

const REJECT_STATES: string[] = [
  WORKFLOW_STATES.RIFIUTATO,
  WORKFLOW_STATES.RIFIUTATO_DITTA,
];

const REJECT_ERROR_MESSAGES: Record<string, string> = {
  [WORKFLOW_STATES.RIFIUTATO]: 'Le note sono obbligatorie per il rifiuto',
  [WORKFLOW_STATES.RIFIUTATO_DITTA]: 'Le note sono obbligatorie per il rifiuto del preventivo',
};

/**
 * Categorie del rifiuto documenti (richiesta committente 2026-07-07).
 * Selezione multipla, almeno una obbligatoria. Si applicano SOLO al rifiuto
 * documenti (RIFIUTATO, controllore HQ/Fastweb) — non al rifiuto preventivo
 * della Ditta. Salvate sulla pratica nel campo array `categorieRifiuto`.
 */
export const CATEGORIE_RIFIUTO: string[] = [
  'PE INFRA / Verifica Statica',
  'PE Impianti',
  'Sicurezza',
  'Revit',
  'Amministrativo',
  'Altro',
];

/**
 * Valida le categorie per il rifiuto documenti.
 * Ritorna null se valido, altrimenti il messaggio di errore.
 * Solo RIFIUTATO le richiede; per gli altri stati sono sempre valide.
 */
export function validateCategorieRifiuto(
  targetState: string,
  categorie: string[] | null | undefined
): string | null {
  if (targetState !== WORKFLOW_STATES.RIFIUTATO) return null;
  if (!categorie || categorie.length === 0) {
    return 'Seleziona almeno una categoria di rifiuto';
  }
  const invalid = categorie.filter((c) => !CATEGORIE_RIFIUTO.includes(c));
  if (invalid.length > 0) {
    return `Categoria di rifiuto non valida: ${invalid.join(', ')}`;
  }
  return null;
}

/**
 * Verifica se una transizione richiede note obbligatorie.
 */
export function transitionRequiresNote(targetState: string): boolean {
  return REJECT_STATES.includes(targetState);
}

/**
 * Valida le note per una transizione di rifiuto.
 * Ritorna null se valido, altrimenti il messaggio di errore.
 */
export function validateRejectNote(targetState: string, note: string | null | undefined): string | null {
  if (!REJECT_STATES.includes(targetState)) return null;
  if (!note || !note.trim()) {
    return REJECT_ERROR_MESSAGES[targetState];
  }
  return null;
}

/**
 * Costruisce gli aggiornamenti Firestore per una transizione, includendo le note nel campo
 * appropriato in base al tipo di rifiuto:
 *  - RIFIUTATO       → noteRifiuto       (rifiuto documenti HQ)
 *  - RIFIUTATO_DITTA → noteRifiutoDitta  (rifiuto preventivo Ditta)
 */
export function buildTransitionUpdates(
  targetState: string,
  note: string | null | undefined
): Record<string, unknown> {
  const updates: Record<string, unknown> = {
    workflowStatus: targetState,
  };
  if (!note) return updates;
  if (targetState === WORKFLOW_STATES.RIFIUTATO) {
    updates.noteRifiuto = note.trim();
  } else if (targetState === WORKFLOW_STATES.RIFIUTATO_DITTA) {
    updates.noteRifiutoDitta = note.trim();
  }
  return updates;
}
